import React from 'react';
import { ShoppingCart, User, Heart, Search, Package, Truck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Template 13: CRAFT - Artisan/Handmade, Warm & Natural (Inspired by Shopify's Craft theme)
const Template13 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-gray-50 min-h-screen font-serif">
            {/* Header */}
                        {/* Header with Navigation */}
            <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="flex justify-between items-center py-4 lg:py-5">
                        <h1 onClick={() => navigate('/template13')} className="text-2xl lg:text-3xl font-bold text-gray-900 cursor-pointer tracking-tight">
                            ESSENCE
                        </h1>
                        <div className="flex items-center gap-4 lg:gap-6">
                            <Search className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <Heart className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template13/cart')}>
                                <ShoppingCart className="w-5 h-5 text-gray-900 hover:text-gray-600 transition-colors" />
                                <span className="absolute -top-2 -right-2 bg-gray-900 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">0</span>
                            </div>
                            <User className="w-5 h-5 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors" onClick={() => navigate('/template13/profile')} />
                        </div>
                    </div>
                    <nav className="border-t border-gray-200">
                        <div className="flex justify-center items-center gap-8 lg:gap-12 py-4">
                            <button onClick={() => navigate('/template13')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Home</button>
                            <button onClick={() => navigate('/template13/shop')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Shop</button>
                            <button onClick={() => navigate('/template13/contact')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Contact</button>
                            <button onClick={() => navigate('/template13/orders')} className="text-sm lg:text-base font-medium text-gray-900 hover:text-gray-600 transition-colors uppercase tracking-wide">Orders</button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Hero - Artisan Story */}
            <section className="relative bg-white">
                <div className="max-w-6xl mx-auto px-6 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <p className="text-sm text-gray-600 mb-4 tracking-wide uppercase">Handcrafted with Love</p>
                            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                                Made by Hand,<br/>Made with Heart
                            </h2>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                Each piece is carefully crafted by skilled artisans using traditional techniques
                                passed down through generations. Discover unique creations that tell a story.
                            </p>
                            <button className="px-8 py-4 bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors rounded-sm">
                                Shop Collection
                            </button>
                        </div>
                        <div className="relative">
                            <img
                                src="https://placehold.co/800x600/E0E0E0/333333?text=Artisan+Craft"
                                alt="Artisan Craft"
                                className="w-full h-[500px] object-cover rounded-sm shadow-xl"
                            />
                            <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-lg rounded-sm max-w-xs">
                                <p className="text-sm text-gray-900 font-medium">
                                    "Every stitch, every detail is a labor of love." - Master Artisan
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Categories */}
            <section className="max-w-6xl mx-auto px-6 py-20">
                <div className="text-center mb-12">
                    <h2 className="text-4xl font-bold text-amber-950 mb-4">Our Collections</h2>
                    <p className="text-lg text-amber-700">Handpicked selections of artisan goods</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { title: 'Pottery', image: 'https://images.unsplash.com/photo-1493106641515-6b5631de4bb9?w=600&h=800&fit=crop', desc: 'Hand-thrown ceramics' },
                        { title: 'Textiles', image: 'https://images.unsplash.com/photo-1558769132-cb1aea6c43e4?w=600&h=800&fit=crop', desc: 'Woven with tradition' },
                        { title: 'Woodwork', image: 'https://images.unsplash.com/photo-1533090161767-e6ffed986c88?w=600&h=800&fit=crop', desc: 'Carved to perfection' }
                    ].map((cat, index) => (
                        <div key={index} className="group cursor-pointer bg-white rounded-sm overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                            <div className="relative overflow-hidden aspect-[3/4]">
                                <img
                                    src={cat.image}
                                    alt={cat.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <div className="p-6 text-center">
                                <h3 className="text-2xl font-bold text-amber-950 mb-2">{cat.title}</h3>
                                <p className="text-amber-700 mb-4">{cat.desc}</p>
                                <button className="text-amber-900 font-medium border-b-2 border-amber-900 hover:border-amber-700 transition-colors">
                                    Browse Collection
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Product Grid */}
            <section className="w-full bg-gray-50 py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-4">All Products</h2>
                        <p className="text-xl text-gray-600">Discover our latest collection</p>
                    </div>

                    <div className="w-full max-w-none">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 lg:gap-8">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(id => (
                                <div key={id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-2xl hover:-translate-y-2 hover:border-gray-900 transition-all duration-300 group">
                                    <div className="aspect-[4/5] overflow-hidden relative bg-gray-50">
                                        <img
                                            src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`}
                                            alt={`Product ${id}`}
                                            className="object-cover object-center w-full h-full group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-4 lg:p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">Product Title {id}</h3>
                                        <p className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">${(69 + id * 10)}.00</p>
                                        <button className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-all duration-300">
                                            Add to Cart
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Promise - Values */}
            <section className="max-w-6xl mx-auto px-6 py-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    {[
                        { icon: Heart, title: 'Handmade', desc: 'Crafted with care by skilled artisans' },
                        { icon: Truck, title: 'Fair Trade', desc: 'Supporting communities worldwide' },
                        { icon: Package, title: 'Sustainable', desc: 'Eco-friendly materials and practices' }
                    ].map((item, index) => (
                        <div key={index} className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 text-white rounded-full mb-4">
                                <item.icon size={28} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                            <p className="text-gray-600">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Artisan Story */}
            <section className="bg-gray-100 py-20">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <img
                        src="https://placehold.co/1200x400/E0E0E0/333333?text=Workshop"
                        alt="Workshop"
                        className="w-full h-64 object-cover rounded-sm mb-8 shadow-lg"
                    />
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">The Craft Behind the Product</h2>
                    <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
                        Our artisans use time-honored techniques combined with modern creativity to produce
                        pieces that are not just products, but works of art. Each item carries the maker's
                        signature and tells its own unique story.
                    </p>
                </div>
            </section>

            {/* Newsletter */}
            <section className="bg-white py-16">
                <div className="max-w-2xl mx-auto px-6 text-center">
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Join Our Craft Community</h3>
                    <p className="text-gray-600 mb-8">Get exclusive access to new collections and artisan stories</p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-3 border-2 border-gray-200 rounded-sm focus:outline-none focus:border-gray-900 text-gray-900"
                        />
                        <button className="px-8 py-3 bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors rounded-sm">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
                        {/* Footer */}
            <footer className="bg-gray-900 text-white mt-0">
                <div className="container mx-auto px-4 lg:px-8 py-12 lg:py-16">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-4 tracking-tight">ESSENCE</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">Contemporary design excellence.</p>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h4>
                            <ul className="space-y-2 text-sm">
                                <li><span onClick={() => navigate('/template13')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Home</span></li>
                                <li><span onClick={() => navigate('/template13/shop')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Shop</span></li>
                                <li><span onClick={() => navigate('/template13/contact')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Contact</span></li>
                                <li><span onClick={() => navigate('/template13/orders')} className="text-gray-400 hover:text-white cursor-pointer transition-colors">Orders</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Customer Service</h4>
                            <ul className="space-y-2 text-sm">
                                <li className="text-gray-400">Shipping & Returns</li>
                                <li className="text-gray-400">Privacy Policy</li>
                                <li className="text-gray-400">Terms & Conditions</li>
                                <li className="text-gray-400">FAQ</li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h4>
                            <p className="text-gray-400 text-sm">hello@essence.com</p>
                        </div>
                    </div>
                    <div className="border-t border-gray-800 mt-12 pt-8">
                        <p className="text-center text-gray-500 text-sm">© 2025 ESSENCE. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template13;
